import type { StaffRole, UserDetail } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { MonitorSmartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { fmtDateTime } from '../../../lib/format.js';
import { EMPTY_VALUE } from '../../detail-fields.js';
import { SegmentedControl } from '../../filters/segmented-control.js';
import { RevokeSessionModal } from '../modals/revoke-session-modal.js';

type Session = UserDetail['sessions'][number];
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

type SessionFilter = 'todas' | 'ativa' | 'revogada';

interface Props {
  readonly sessions: readonly Session[];
  readonly userId: string;
  readonly staffRole: StaffRole;
}

export function SessionsTab({ sessions, userId, staffRole }: Props) {
  const copy = useUsersContent();
  const canManage = MANAGE_ROLES.includes(staffRole);
  const [revokeSessionId, setRevokeSessionId] = useState<string | null>(null);
  const [filter, setFilter] = useState<SessionFilter>('todas');

  const { rows, counts } = useMemo(() => {
    const active = sessions.filter((s) => s.revokedAt === null);
    const revoked = sessions.filter((s) => s.revokedAt !== null);
    return {
      counts: { todas: sessions.length, ativa: active.length, revogada: revoked.length },
      rows: filter === 'ativa' ? active : filter === 'revogada' ? revoked : sessions,
    };
  }, [sessions, filter]);

  if (sessions.length === 0) {
    return (
      <EmptyState icon={<MonitorSmartphone className="size-6" />} title={copy.sessionsTab.empty} />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl<SessionFilter>
          ariaLabel={copy.sessionsTab.filterAriaLabel}
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'todas', label: copy.sessionsTab.optionAll, count: counts.todas },
            { value: 'ativa', label: copy.sessionsTab.optionActive, count: counts.ativa },
            { value: 'revogada', label: copy.sessionsTab.optionRevoked, count: counts.revogada },
          ]}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<MonitorSmartphone className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.sessionsTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={() => setFilter('todas')}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{copy.sessionsTab.columnDevice}</TableHead>
              <TableHead>{copy.sessionsTab.columnOrg}</TableHead>
              <TableHead>{copy.sessionsTab.columnCreatedAt}</TableHead>
              <TableHead>{copy.sessionsTab.columnLastUsed}</TableHead>
              <TableHead>{copy.sessionsTab.columnExpires}</TableHead>
              <TableHead>{copy.sessionsTab.columnStatus}</TableHead>
              {canManage && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((session) => (
              <TableRow key={session.id}>
                <TableCell className="text-foreground">
                  {session.deviceName ?? session.deviceId.slice(0, 12)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {session.orgId ?? EMPTY_VALUE}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {fmtDateTime(session.createdAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {fmtDateTime(session.lastUsedAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {fmtDateTime(session.expiresAt)}
                </TableCell>
                <TableCell>
                  {session.revokedAt === null ? (
                    <StatusBadge variant="success">{copy.sessionsTab.statusActive}</StatusBadge>
                  ) : (
                    <StatusBadge variant="neutral">{copy.sessionsTab.statusRevoked}</StatusBadge>
                  )}
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    {session.revokedAt === null && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeSessionId(session.id)}
                      >
                        {copy.sessionsTab.revokeAction}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {revokeSessionId !== null && (
        <RevokeSessionModal
          open
          onClose={() => setRevokeSessionId(null)}
          userId={userId}
          sessionId={revokeSessionId}
        />
      )}
    </div>
  );
}
